import type { Profile } from '@interlucent/organization-protocol';
import type { ProfileValidation, ProfileFormData } from '../types/profile.types';

/**
 * Profile business logic and validation
 * Pure functions for validation and transformation
 */

export const profileService = {
  /**
   * Generate profile slug from name
   * Converts to lowercase, replaces non-alphanumeric with hyphens
   */
  generateSlug: (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  },

  /**
   * Validate profile name
   */
  validateProfileName: (name: string): { valid: boolean; error?: string } => {
    const trimmed = name.trim();
    if (!trimmed) {
      return { valid: false, error: "Profile name is required" };
    }
    if (trimmed.length < 3) {
      return { valid: false, error: "Name must be at least 3 characters" };
    }
    if (trimmed.length > 50) {
      return { valid: false, error: "Name must be less than 50 characters" };
    }
    if (!/^[a-zA-Z0-9\s\-_]+$/.test(trimmed)) {
      return {
        valid: false,
        error: "Name can only contain letters, numbers, spaces, hyphens, and underscores"
      };
    }
    return { valid: true };
  },

  /**
   * Validate profile slug
   */
  validateProfileSlug: (slug: string): { valid: boolean; error?: string } => {
    if (!slug) {
      return { valid: true }; // Slug is optional
    }
    const trimmed = slug.trim();
    if (trimmed.length < 3) {
      return { valid: false, error: "Slug must be at least 3 characters" };
    }
    if (trimmed.length > 50) {
      return { valid: false, error: "Slug must be less than 50 characters" };
    }
    if (!/^[a-z0-9\-]+$/.test(trimmed)) {
      return {
        valid: false,
        error: "Slug can only contain lowercase letters, numbers, and hyphens"
      };
    }
    if (trimmed.startsWith('-') || trimmed.endsWith('-')) {
      return {
        valid: false,
        error: "Slug cannot start or end with a hyphen"
      };
    }
    return { valid: true };
  },

  /**
   * Validate complete profile form data
   */
  validateProfileForm: (data: ProfileFormData): ProfileValidation => {
    const errors: ProfileValidation['errors'] = {};
    let valid = true;

    const nameValidation = profileService.validateProfileName(data.name);
    if (!nameValidation.valid) {
      errors.name = nameValidation.error;
      valid = false;
    }

    if (data.profileSlug) {
      const slugValidation = profileService.validateProfileSlug(data.profileSlug);
      if (!slugValidation.valid) {
        errors.profileSlug = slugValidation.error;
        valid = false;
      }
    }

    if (!data.type) {
      errors.type = "Profile type is required";
      valid = false;
    }

    if (!data.region || data.region.trim().length === 0) {
      errors.region = "Region is required";
      valid = false;
    }

    return { valid, errors };
  },

  /**
   * Check if profile name is unique within project
   */
  isNameUnique: (
    name: string,
    projectId: string,
    profiles: Record<string, Profile>,
    excludeId?: string
  ): boolean => {
    const normalized = name.toLowerCase().trim();
    return !Object.values(profiles).some(
      p =>
        p.projectId === projectId &&
        p.id !== excludeId &&
        !p.deletedAt &&
        p.name.toLowerCase().trim() === normalized
    );
  },

  /**
   * Check if profile slug is unique within project
   */
  isSlugUnique: (
    slug: string,
    projectId: string,
    profiles: Record<string, Profile>,
    excludeId?: string
  ): boolean => {
    const normalized = slug.toLowerCase().trim();
    return !Object.values(profiles).some(
      p =>
        p.projectId === projectId &&
        p.id !== excludeId &&
        !p.deletedAt &&
        p.profileSlug?.toLowerCase().trim() === normalized
    );
  },

  /**
   * Get profiles for a specific project (excludes soft-deleted by default)
   */
  getProjectProfiles: (
    projectId: string,
    profiles: Record<string, Profile>,
    includeDeleted: boolean = false
  ): Profile[] => {
    return Object.values(profiles)
      .filter(p => p.projectId === projectId && (includeDeleted || !p.deletedAt))
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  /**
   * Get active profiles for a specific project
   */
  getActiveProfiles: (
    projectId: string,
    profiles: Record<string, Profile>
  ): Profile[] => {
    return profileService
      .getProjectProfiles(projectId, profiles)
      .filter(p => p.active);
  },

  /**
   * Format profile type for display
   */
  formatProfileType: (type: string): string => {
    switch (type) {
      case "by_request":
        return "On-Demand";
      case "coop_shared_pool":
        return "Shared Pool";
      case "private_pool":
        return "Private Pool";
      case "independent":
        return "Independent";
      default:
        return type;
    }
  },

  /**
   * Get profile type description
   */
  getProfileTypeDescription: (type: string): string => {
    switch (type) {
      case "by_request":
        return "Resources allocated on-demand when needed";
      case "coop_shared_pool":
        return "Shared resource pool across organization";
      case "private_pool":
        return "Dedicated resources for this profile";
      default:
        return "";
    }
  }
};
